import React, { useState } from 'react';
import { FormResponse } from '../types';
import {
  Container,
  Card,
  Button,
  Title,
  Subtitle
} from '../styles/GlobalStyles';
import styled from 'styled-components';

const DetailCard = styled(Card)`
  margin-bottom: 24px;
  
  h3 {
    color: #212529;
    margin-bottom: 16px;
    font-size: 18px;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 8px;
  }
  
  .detail-item {
    margin-bottom: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f8f9fa;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
  }
  
  .value {
    color: #212529;
  }
  
  .boolean-value {
    color: #0d6efd;
    font-weight: 500;
  }
  
  .array-value {
    color: #6f42c1;
  }
`;

const BackButton = styled(Button)`
  background: #6c757d;
  margin-bottom: 24px;
  
  &:hover {
    background: #5a6268;
  }
`;

const TabsContainer = styled.div`
  margin-bottom: 24px;
`;

const TabsList = styled.div`
  display: flex;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
  overflow-x: auto;
  gap: 4px;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? '#0d6efd' : 'transparent'};
  color: ${props => props.active ? 'white' : '#495057'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  
  &:hover {
    background: ${props => props.active ? '#0b5ed7' : '#f8f9fa'};
  }
`;

const TabContent = styled.div`
  min-height: 200px;
`;

interface ResponseDetailsProps {
  response: FormResponse;
  onBack: () => void;
}

const ResponseDetails: React.FC<ResponseDetailsProps> = ({ response, onBack }) => {
  const [activeTab, setActiveTab] = useState('dadosBasicos');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Definição das categorias e suas respectivas perguntas
  const questionCategories = {
    dadosBasicos: {
      title: 'Dados Básicos',
      fields: [
        'nomeOrganizacao', 'cnpj', 'endereco', 'telefone', 'email',
        'nomeResponsavel', 'cargoResponsavel', 'anoFundacao', 'atividades'
      ]
    },
    estruturaOrganizacional: {
      title: 'Estrutura Organizacional',
      fields: [
        'numeroFuncionarios', 'numeroVoluntarios', 'pessoasAtendidasMes',
        'suspendeAtividades', 'mesesSuspensao', 'documentos'
      ]
    },
    publicoAtendido: {
      title: 'Público Atendido',
      fields: [
        'publicoAtendido', 'publicoAtendidoOutros', 'criancas', 'adolescentes',
        'jovens', 'adultos', 'idosos', 'possuiFichaCadastral', 'beneficiariosMensais'
      ]
    },
    infraestrutura: {
      title: 'Infraestrutura',
      fields: [
        'infraestrutura', 'possuiTransporte', 'tipoTransporte', 'veiculoColeta'
      ]
    },
    alimentacao: {
      title: 'Alimentação e Refeições',
      fields: [
        'serveRefeicoes', 'serveCafeManha', 'pessoasCafeManha', 'diasCafeManha',
        'serveLancheManha', 'pessoasLancheManha', 'diasLancheManha',
        'serveAlmoco', 'pessoasAlmoco', 'diasAlmoco',
        'serveLancheTarde', 'pessoasLancheTarde', 'diasLancheTarde',
        'serveJantar', 'pessoasJantar', 'diasJantar'
      ]
    },
    distribuicaoAlimentos: {
      title: 'Distribuição de Alimentos',
      fields: [
        'distribuiAlimentos', 'distribuiCestasBasicas', 'quantidadeCestasBasicas',
        'distribuiMarmitas', 'quantidadeMarmitas', 'distribuiKitFrutas',
        'quantidadeKitFrutas', 'atividadesAlimentacao', 'tiposAtividadesAlimentacao',
        'outrasAtividadesAlimentacao', 'descricaoAtividadesAlimentacao'
      ]
    },
    parcerias: {
      title: 'Parcerias e Doações',
      fields: [
        'transfereDoacoes', 'unidadesBeneficiadas', 'recebeDoacoesOutrosParceiros',
        'tiposAlimentosRecebidos', 'outrosAlimentosRecebidos', 'recebeBancoAlimentos',
        'quaisBancosAlimentos', 'recebeSescMesaBrasil', 'quaisUnidadesSesc',
        'recebeGoverno', 'recebePessoasFisicas', 'frequenciaDoacoesOutrosParceiros'
      ]
    },
    logistica: {
      title: 'Logística e Coleta',
      fields: [
        'restricoesColeta', 'descricaoRestricoes', 'disponibilidadeColeta'
      ]
    },
    avaliacao: {
      title: 'Avaliação',
      fields: [
        'avaliacaoFormulario', 'comentarioExperiencia', 'probabilidadeRecomendacao'
      ]
    }
  };

  // Função para filtrar campos dependentes baseado nas condições
  const filterDependentFields = (data: any) => {
    const filteredData = { ...data };
    
    // Regras de dependência
    const dependencyRules = [
      // Distribuição de alimentos
      {
        parentField: 'distribuicaoAlimentos',
        parentValue: 'Não',
        dependentFields: [
          'tipoAlimentoDistribuido', 'frequenciaDistribuicao', 'numeroFamiliasAtendidas',
          'parceirosFornecedores', 'origemAlimentos', 'criteriosSelecao',
          'acompanhamentoNutricional', 'educacaoNutricional'
        ]
      },
      // Fornecimento de refeições
      {
        parentField: 'fornecimentoRefeicoes',
        parentValue: 'Não',
        dependentFields: [
          'tipoRefeicao', 'numeroRefeicoesServidas', 'numeroRefeicoesServidas',
          'localRefeicoes', 'horarioRefeicoes', 'acompanhamentoNutricionalRefeicoes'
        ]
      },
      // Outras atividades
      {
        parentField: 'outrasAtividades',
        parentValue: 'Não',
        dependentFields: ['descricaoOutrasAtividades']
      }
    ];
    
    // Aplicar regras de filtragem
    dependencyRules.forEach(rule => {
      if (filteredData[rule.parentField] === rule.parentValue) {
        rule.dependentFields.forEach(field => {
          delete filteredData[field];
        });
      }
    });
    
    return filteredData;
  };

  const formatValue = (value: any) => {
    if (typeof value === 'boolean') {
      return <span className="boolean-value">{value ? 'Sim' : 'Não'}</span>;
    }
    if (Array.isArray(value)) {
      return <span className="array-value">{value.join(', ')}</span>;
    }
    if (typeof value === 'number') {
      return <span className="value">{value.toLocaleString('pt-BR')}</span>;
    }
    return <span className="value">{value || 'Não informado'}</span>;
  };

  const getFieldLabel = (key: string) => {
    const labels: { [key: string]: string } = {
      nomeOrganizacao: 'Nome da Organização',
      cnpj: 'CNPJ',
      endereco: 'Endereço',
      telefone: 'Telefone',
      email: 'E-mail',
      nomeResponsavel: 'Nome do Responsável',
      cargoResponsavel: 'Cargo do Responsável',
      anoFundacao: 'Ano de Fundação',
      atividades: 'Atividades da OSC',
      numeroFuncionarios: 'Número de funcionários',
      numeroVoluntarios: 'Número de voluntários',
      pessoasAtendidasMes: 'Pessoas atendidas por mês',
      suspendeAtividades: 'Suspende atividades em algum período',
      mesesSuspensao: 'Meses de suspensão das atividades',
      documentos: 'Documentos que a entidade possui',
      publicoAtendido: 'Público atendido',
      publicoAtendidoOutros: 'Outros públicos atendidos',
      criancas: 'Crianças (0 a 12 anos)',
      adolescentes: 'Adolescentes (13 a 17 anos)',
      jovens: 'Jovens (18 a 29 anos)',
      adultos: 'Adultos (30 a 59 anos)',
      idosos: 'Pessoas Idosas (acima dos 60 anos)',
      possuiFichaCadastral: 'Possui ficha cadastral com dados socioeconômicos dos atendidos',
      beneficiariosMensais: 'Beneficiários mensais (alimentos)',
      infraestrutura: 'Infraestrutura que a entidade possui',
      serveRefeicoes: 'Servem refeições no local',
      serveCafeManha: 'Servem Café da manhã',
      pessoasCafeManha: 'Pessoas servidas - Café da manhã (mês)',
      diasCafeManha: 'Dias por semana - Café da manhã',
      serveLancheManha: 'Servem Lanche da manhã',
      pessoasLancheManha: 'Pessoas servidas - Lanche da manhã (mês)',
      diasLancheManha: 'Dias por semana - Lanche da manhã',
      serveAlmoco: 'Servem Almoço',
      pessoasAlmoco: 'Pessoas servidas - Almoço (mês)',
      diasAlmoco: 'Dias por semana - Almoço',
      serveLancheTarde: 'Servem Lanche da Tarde',
      pessoasLancheTarde: 'Pessoas servidas - Lanche da tarde (mês)',
      diasLancheTarde: 'Dias por semana - Lanche da tarde',
      serveJantar: 'Servem Jantar',
      pessoasJantar: 'Pessoas servidas - Jantar (mês)',
      diasJantar: 'Dias por semana - Jantar',
      distribuiAlimentos: 'Distribui Alimentos',
      distribuiCestasBasicas: 'Distribui Cestas Básicas',
      quantidadeCestasBasicas: 'Quantidade de Cestas Básicas por Mês',
      distribuiMarmitas: 'Distribui Marmitas/Quentinhas',
      quantidadeMarmitas: 'Quantidade de Marmitas/Quentinhas por Mês',
      distribuiKitFrutas: 'Distribui Kit/Sacolinha de Frutas, Legumes, Verduras',
      quantidadeKitFrutas: 'Quantidade de Kit/Sacolinha por Mês',
      atividadesAlimentacao: 'Atividades Voltadas para Alimentação',
      tiposAtividadesAlimentacao: 'Tipos de Atividades de Alimentação',
      outrasAtividadesAlimentacao: 'Outras Atividades de Alimentação',
      descricaoAtividadesAlimentacao: 'Descrição das Atividades de Alimentação',
      transfereDoacoes: 'Transfere Doações para Outras Unidades',
      unidadesBeneficiadas: 'Unidades/Filiais Beneficiadas',
      restricoesColeta: 'Possui Restrições para Coletar Doações',
      descricaoRestricoes: 'Descrição das Restrições',
      disponibilidadeColeta: 'Disponibilidade para Coletar Doações',
      recebeDoacoesOutrosParceiros: 'Recebe Doações de Outros Parceiros',
      tiposAlimentosRecebidos: 'Tipos de Alimentos Recebidos',
      outrosAlimentosRecebidos: 'Outros Alimentos Recebidos',
      recebeBancoAlimentos: 'Recebe Doação de Banco de Alimentos',
      quaisBancosAlimentos: 'Quais Bancos de Alimentos',
      recebeSescMesaBrasil: 'Recebe Doação do Sesc Mesa Brasil',
      quaisUnidadesSesc: 'Quais Unidades do Sesc Mesa Brasil',
      recebeGoverno: 'Recebe Doação do Governo',
      recebePessoasFisicas: 'Recebe Doação de Pessoas Físicas',
      frequenciaDoacoesOutrosParceiros: 'Frequência de Doações de Outros Parceiros',
      possuiTransporte: 'Possui Transporte',
      tipoTransporte: 'Tipo de Transporte',
      veiculoColeta: 'Veículo para Coleta',
      avaliacaoFormulario: 'Avaliação do Formulário',
      comentarioExperiencia: 'Comentário sobre a Experiência',
      probabilidadeRecomendacao: 'Probabilidade de Recomendação'
    };
    return labels[key] || key;
  };

  const excludeFields = ['id', 'oscId', 'createdAt'];
  
  // Aplicar filtragem de campos dependentes
  const filteredResponse = filterDependentFields(response);
  
  // Função para obter dados de uma categoria específica
  const getCategoryData = (categoryKey: string) => {
    const category = questionCategories[categoryKey as keyof typeof questionCategories];
    if (!category) return [];
    
    return category.fields
      .filter(field => filteredResponse.hasOwnProperty(field) && !excludeFields.includes(field))
      .map(field => [field, filteredResponse[field as keyof FormResponse]])
      .filter(([, value]) => value !== undefined && value !== null && value !== '');
  };
  
  // Função para renderizar os campos de uma categoria
  const renderCategoryFields = (categoryKey: string) => {
    const categoryData = getCategoryData(categoryKey);
    
    if (categoryData.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
          Nenhuma informação disponível para esta categoria.
        </div>
      );
    }
    
    return (
      <DetailCard>
        <h3>{questionCategories[categoryKey as keyof typeof questionCategories].title}</h3>
        {categoryData.map(([key, value], index) => (
          <div key={String(key) || `field-${index}`} className="detail-item">
            <div className="label">{getFieldLabel(key as string)}</div>
            <div>{formatValue(value)}</div>
          </div>
        ))}
      </DetailCard>
    );
  };

  return (
    <Container>
      <BackButton onClick={onBack}>
        ← Voltar à tela inicial
      </BackButton>
      
      <Title>Detalhes da Resposta</Title>
      <Subtitle>Resposta #{response.id || 'N/A'} - {formatDate(response.createdAt)}</Subtitle>
      
      <TabsContainer>
        <TabsList>
          {Object.entries(questionCategories).map(([key, category]) => {
            const hasData = getCategoryData(key).length > 0;
            return (
              <TabButton
                key={key}
                active={activeTab === key}
                onClick={() => setActiveTab(key)}
                style={{ opacity: hasData ? 1 : 0.6 }}
              >
                {category.title}
                {hasData && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '10px' }}>
                    {getCategoryData(key).length}
                  </span>
                )}
              </TabButton>
            );
          })}
        </TabsList>
        
        <TabContent>
          {renderCategoryFields(activeTab)}
        </TabContent>
      </TabsContainer>
    </Container>
  );
};

export default ResponseDetails;